import { FEVER_C, METRIC_META } from '@oncocoach/care';
import type { MetricKind, PatientView } from '../../lib/types';
import { TEMP_CLASS, classifyTemp, describeSymptom, fmtAgo, fmtTemp } from '../../lib/format';
import { Icon } from '../../components/Mark';
import { Card } from '../../components/Bits';
import { TempChart } from '../../components/TempChart';

const LOOK: Record<MetricKind, { icon: Parameters<typeof Icon>[0]['name']; tone: string }> = {
  'weight-lbs': { icon: 'profile', tone: 'blue' },
  'heart-rate': { icon: 'health', tone: 'red' },
  'sleep-hours': { icon: 'star', tone: 'purple' },
  'exertion-minutes': { icon: 'steps', tone: 'green' },
  'intake-ml': { icon: 'drop', tone: 'blue' },
  'bowel-movements': { icon: 'symptoms', tone: 'gold' },
};

/** Health: the temperature record first, then the numbers this patient's track actually asks for. */
export function HealthTab({ view }: { view: PatientView }) {
  const m = view.metrics;
  const tiles = view.track.metrics.map((kind: MetricKind) => {
    const meta = METRIC_META[kind];
    const sample = m[kind];
    return {
      kind,
      icon: LOOK[kind].icon,
      tone: LOOK[kind].tone,
      label: meta.label,
      unit: meta.unit,
      value: sample ? sample.value.toFixed(meta.decimals) : '—',
    };
  });
  const days = view.trend.filter((t) => t.celsius !== null).slice().reverse();
  const latest = view.latestTemperature;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Your record</h1>
          <span className="hint">The last seven days{view.cycle ? ` · cycle ${view.cycle.cycle.number}, day ${view.cycle.day}` : ''}</span>
        </div>
        <a className="button primary" href="#/temp/log">
          <Icon name="temperature" size={16} /> Log a temperature
        </a>
      </div>

      <div className="metric-row">
        {tiles.map((t) => (
          <div className="tile" key={t.kind}>
            <span className={`bubble bubble-${t.tone} bubble-sm tile-icon`}>
              <Icon name={t.icon} size={17} />
            </span>
            <strong className={`tile-value tone-${t.tone}`}>
              {t.value} <small>{t.unit}</small>
            </strong>
            <span className="tile-label">{t.label}</span>
          </div>
        ))}
      </div>

      <div className="grid-2">
        <Card>
          <div className="card-head">
            <div>
              <h2>Temperature</h2>
              <span className="hint">Seven-day trend, highest of each day</span>
            </div>
            <span className={`pill ${latest && latest.celsius >= FEVER_C ? 'pill-red' : 'pill-soft'}`}>
              <strong>{latest ? `${fmtTemp(latest.celsius)}C` : '—'}</strong>
            </span>
          </div>
          <TempChart trend={view.trend} />
          {view.track.feverIsEmergency ? <p className="hint">{FEVER_C.toFixed(1)}°C or more on treatment means ringing {view.practiceName}'s emergency line, whatever time it is.</p> : null}
        </Card>

        <div className="stack">
          <Card>
            <h2>Recent temperatures</h2>
            {days.length === 0 ? <p className="hint">Nothing logged in the last week.</p> : null}
            <ul className="reading-list">
              {days.map((t, i) => {
                const celsius = t.celsius as number;
                const cls = TEMP_CLASS[classifyTemp(celsius)];
                return (
                  <li key={i}>
                    <span className="row-main">
                      <strong>{fmtTemp(celsius)}C</strong>
                      <span className="hint">
                        {t.label}
                        {t.at ? ` · ${fmtAgo(t.at, view.now)}` : ''}
                      </span>
                    </span>
                    <span className={`badge ${cls?.tone === 'good' ? 'badge-green' : cls?.tone === 'bad' ? 'badge-red' : 'badge-gold'}`}>{cls?.label}</span>
                  </li>
                );
              })}
            </ul>
          </Card>

          <Card>
            <h2>What you have told us</h2>
            {view.recentSymptoms.length === 0 ? <p className="hint">Nothing reported in the last two days.</p> : null}
            <ul className="reading-list">
              {view.recentSymptoms.slice(0, 6).map((r, i) => (
                <li key={i}>
                  <span className="row-main">
                    <strong>{r.symptoms.length > 0 ? r.symptoms.map(describeSymptom).join(', ') : 'Nothing to report'}</strong>
                    <span className="hint">
                      {fmtAgo(r.at, view.now)}
                      {r.note ? ` · “${r.note}”` : ''}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            <a className="button secondary small" href="#/symptoms">
              Tell them how you are today
            </a>
          </Card>

          <Card>
            <h2>Connected devices</h2>
            <p className="hint">A thermometer, a scale or a watch can fill in some of these for you. What a device sends is recorded, but it never decides on its own that something is wrong.</p>
            <a className="button secondary small" href="#/devices">
              Connect a device
            </a>
          </Card>
        </div>
      </div>
    </>
  );
}
